/**
 * The sync engine: keeps this device's copy of a group in step with the server, and never
 * loses a change made offline.
 *
 *   enqueue(cmd)  saves the change to the outbox (IndexedDB) first, then tries to send it
 *   syncOnce()    one round trip: send up to 50 waiting changes, receive every newer event
 *
 * When the network fails, it waits 1 s, 2 s, 4 s ... up to 5 minutes (with a little random
 * jitter so many phones don't retry in lockstep) and tries again. Coming back online or
 * reopening the app retries right away. Each change carries a unique cmdId, so if a request
 * actually reached the server before the connection dropped, the retry is recognized and
 * nothing is applied twice.
 */
import type { Command, StoredEvent } from "@split/core";
import { ApiError, NetworkError, api } from "./api.ts";
import * as local from "./db.ts";
import type { LocalDB, OutboxEntry } from "./db.ts";

export type SyncStatus = "idle" | "syncing" | "offline" | "signed-out" | "error";

export interface SyncResponse {
  results: (
    | { cmdId: string; status: "applied" | "duplicate"; seqs: number[] }
    | { cmdId: string; status: "rejected"; code: string; message: string }
  )[];
  events: StoredEvent[];
  cursor: number;
  more: boolean;
}

export type SyncTransport = (
  groupId: string,
  body: { cursor: number; commands: { cmdId: string; cmd: Command }[] },
) => Promise<SyncResponse>;

export const httpTransport: SyncTransport = (groupId, body) =>
  api<SyncResponse>(`/api/groups/${encodeURIComponent(groupId)}/sync`, { body, timeoutMs: 20_000 });

export const BATCH = 50;
export const MIN_BACKOFF = 1_000;
export const MAX_BACKOFF = 300_000;

export interface SyncOptions {
  transport?: SyncTransport;
  onChange?: () => void;
  /** Rejections are reported here so the screen can explain them. */
  onRejected?: (entry: OutboxEntry) => void;
  random?: () => number;
  schedule?: (fn: () => void, ms: number) => unknown;
  cancel?: (handle: unknown) => void;
  newId?: () => string;
  now?: () => string;
}

export class SyncEngine {
  events: StoredEvent[] = [];
  outbox: OutboxEntry[] = [];
  cursor = 0;
  status: SyncStatus = "idle";
  lastSyncedAt: string | null = null;
  lastError: string | null = null;
  nextRetryAt: number | null = null;

  private backoff = MIN_BACKOFF;
  private running: Promise<void> | null = null;
  private again = false;
  private timer: unknown = null;
  private stopped = false;
  private readonly o: Required<SyncOptions>;

  constructor(
    private readonly db: LocalDB,
    readonly groupId: string,
    options: SyncOptions = {},
  ) {
    this.o = {
      transport: httpTransport,
      onChange: () => {},
      onRejected: () => {},
      random: Math.random,
      schedule: (fn, ms) => setTimeout(fn, ms),
      cancel: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
      newId: () => crypto.randomUUID(),
      now: () => new Date().toISOString(),
      ...options,
    };
  }

  private get cursorKey() {
    return `cursor:${this.groupId}`;
  }

  /** Reads this device's saved copy (all at once, so it's never half old, half new). Call once before anything else. */
  async load(): Promise<void> {
    const saved = await local.loadGroup(this.db, this.groupId, {
      cursor: this.cursorKey,
      synced: `synced:${this.groupId}`,
    });
    this.events = saved.events;
    this.outbox = saved.outbox;
    this.cursor = saved.cursor ?? this.events.at(-1)?.seq ?? 0;
    this.lastSyncedAt = saved.synced ?? null;
    this.o.onChange();
  }

  get pending(): OutboxEntry[] {
    return this.outbox.filter((e) => e.status === "pending");
  }

  get failed(): OutboxEntry[] {
    return this.outbox.filter((e) => e.status === "failed");
  }

  /** Saves a change locally, then sends it as soon as possible. Returns its cmdId. */
  async enqueue(cmd: Command): Promise<string> {
    const entry: OutboxEntry = {
      cmdId: this.o.newId(),
      groupId: this.groupId,
      cmd: plain(cmd),
      createdAt: this.o.now(),
      status: "pending",
    };
    await local.putOutbox(this.db, entry);
    this.outbox = [...this.outbox, entry];
    this.o.onChange();
    this.kick();
    return entry.cmdId;
  }

  /** Puts many changes in the outbox at once (used by the Sheet import). */
  async enqueueMany(cmds: { cmdId: string; cmd: Command }[]): Promise<void> {
    const base = Date.parse(this.o.now());
    const entries = cmds.map((c, i) => ({
      cmdId: c.cmdId,
      groupId: this.groupId,
      cmd: plain(c.cmd),
      createdAt: new Date(base + i).toISOString(),
      status: "pending" as const,
    }));
    for (const e of entries) await local.putOutbox(this.db, e);
    const known = new Set(this.outbox.map((e) => e.cmdId));
    this.outbox = [...this.outbox, ...entries.filter((e) => !known.has(e.cmdId))];
    this.o.onChange();
    this.kick();
  }

  async discard(cmdId: string): Promise<void> {
    await local.deleteOutbox(this.db, cmdId);
    this.outbox = this.outbox.filter((e) => e.cmdId !== cmdId);
    this.o.onChange();
  }

  /** Asks for a sync now (coalesces with one already running). */
  kick(): void {
    if (this.stopped) return;
    if (this.timer !== null) {
      this.o.cancel(this.timer);
      this.timer = null;
    }
    this.nextRetryAt = null;
    void this.syncOnce();
  }

  stop(): void {
    this.stopped = true;
    if (this.timer !== null) this.o.cancel(this.timer);
  }

  /** One full sync: repeats until nothing is waiting and the server has nothing newer. */
  syncOnce(): Promise<void> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = this.loop().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  private async loop(): Promise<void> {
    do {
      this.again = false;
      try {
        this.setStatus("syncing");
        let more = true;
        while (more && !this.stopped) {
          const batch = this.pending.slice(0, BATCH);
          const res = await this.o.transport(this.groupId, {
            cursor: this.cursor,
            commands: batch.map((e) => ({ cmdId: e.cmdId, cmd: e.cmd })),
          });
          await this.receive(res);
          more = res.more || this.pending.some((e) => !batch.includes(e));
        }
        this.backoff = MIN_BACKOFF;
        this.lastError = null;
        this.lastSyncedAt = this.o.now();
        await local.setMeta(this.db, `synced:${this.groupId}`, this.lastSyncedAt);
        this.setStatus("idle");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          this.setStatus("signed-out");
          return;
        }
        this.lastError = e instanceof Error ? e.message : String(e);
        this.setStatus(e instanceof NetworkError ? "offline" : "error");
        this.scheduleRetry();
        return;
      }
    } while (this.again && !this.stopped);
  }

  private async receive(res: SyncResponse): Promise<void> {
    for (const r of res.results) {
      const entry = this.outbox.find((e) => e.cmdId === r.cmdId);
      if (!entry) continue;
      if (r.status === "rejected") {
        const failed: OutboxEntry = { ...entry, status: "failed", error: r.message };
        await local.putOutbox(this.db, failed);
        this.outbox = this.outbox.map((e) => (e.cmdId === r.cmdId ? failed : e));
        this.o.onRejected(failed);
      } else {
        await local.deleteOutbox(this.db, r.cmdId);
        this.outbox = this.outbox.filter((e) => e.cmdId !== r.cmdId);
      }
    }
    // Never the same event twice, even if another tab or a background catch-up saved some.
    const have = Math.max(this.cursor, this.events.at(-1)?.seq ?? 0);
    const fresh = res.events.filter((e) => e.seq > have);
    if (fresh.length > 0 || res.cursor !== this.cursor) {
      await local.saveEvents(this.db, fresh, this.cursorKey, Math.max(res.cursor, this.cursor));
      this.events = [...this.events, ...fresh];
      this.cursor = Math.max(res.cursor, this.cursor);
    }
    this.o.onChange();
  }

  private scheduleRetry(): void {
    if (this.stopped) return;
    const jitter = 0.8 + this.o.random() * 0.4;
    const wait = Math.round(this.backoff * jitter);
    this.nextRetryAt = Date.now() + wait;
    this.backoff = Math.min(MAX_BACKOFF, this.backoff * 2);
    this.timer = this.o.schedule(() => {
      this.timer = null;
      this.nextRetryAt = null;
      void this.syncOnce();
    }, wait);
    this.o.onChange();
  }

  private setStatus(s: SyncStatus): void {
    if (this.status !== s) {
      this.status = s;
      this.o.onChange();
    }
  }
}

/**
 * A plain JSON copy. UI frameworks wrap objects in proxies that IndexedDB can't store, and the
 * outbox must only ever hold plain data.
 */
function plain<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
